import { useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Plus,
  Filter,
  RefreshCw,
  ChevronDown,
  X,
  Download,
  FileText,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/app/components/ui/pagination';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/app/components/ui/collapsible';
import { Label } from '@/app/components/ui/label';
import { useInspections, useInspectionSummary, useExportInspectionsCsv } from '@/hooks/use-inspections';
import { usePermissions } from '@/hooks/use-permissions';
import { useAssets } from '@/hooks/use-assets';
import { formatDateOnly } from '@/lib/date-utils';
import type { Inspection, InspectionResult } from '@/lib/types';

const resultColors: Record<InspectionResult, string> = {
  pass: 'bg-green-100 text-green-800',
  fail: 'bg-red-100 text-red-800',
  conditional: 'bg-yellow-100 text-yellow-800',
};

const resultIcons: Record<InspectionResult, React.ReactNode> = {
  pass: <CheckCircle className="h-4 w-4" />,
  fail: <XCircle className="h-4 w-4" />,
  conditional: <AlertTriangle className="h-4 w-4" />,
};

const PAGE_SIZE = 20;

export function InspectionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);

  const page = parseInt(searchParams.get('page') || '1', 10);
  const result = (searchParams.get('result') || '') as InspectionResult | '';
  const dateAfter = searchParams.get('date_after') || '';
  const dateBefore = searchParams.get('date_before') || '';
  const assetId = searchParams.get('asset') || '';

  const { data, isLoading, isError, refetch } = useInspections({
    page,
    page_size: PAGE_SIZE,
    result,
    date_after: dateAfter,
    date_before: dateBefore,
    asset: assetId ? parseInt(assetId, 10) : '',
  });

  const { data: summaryData, isLoading: summaryLoading } = useInspectionSummary({
    result,
    date_after: dateAfter,
    date_before: dateBefore,
    asset: assetId ? parseInt(assetId, 10) : '',
  });

  const { data: assetsData } = useAssets({ page_size: 100 });
  const exportMutation = useExportInspectionsCsv();
  const { canRecordInspections } = usePermissions();

  const inspections = data?.results || [];
  const totalCount = data?.count || 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const updateSearchParams = useCallback(
    (updates: Record<string, string>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      if (!updates.page) {
        newParams.set('page', '1');
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const clearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  const handleExportCsv = useCallback(async () => {
    try {
      const blob = await exportMutation.mutateAsync({
        result,
        date_after: dateAfter,
        date_before: dateBefore,
        asset: assetId ? parseInt(assetId, 10) : '',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inspections-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to export CSV:', error);
    }
  }, [exportMutation, result, dateAfter, dateBefore, assetId]);

  const hasActiveFilters = result || dateAfter || dateBefore || assetId;

  const formatDate = (dateString: string) => {
    return formatDateOnly(dateString, '—', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getAssetName = (assetIdNum: number) => {
    const asset = assetsData?.results?.find((a) => a.id === assetIdNum);
    return asset ? asset.display_name : `Asset #${assetIdNum}`;
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Inspections</h1>
          <p className="text-gray-600 mt-1">
            Manage equipment inspections and quality control
            {totalCount > 0 && ` (${totalCount} total)`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={handleExportCsv}
            disabled={exportMutation.isPending || totalCount === 0 || !canRecordInspections}
            title={canRecordInspections ? undefined : 'Requires Inspection Certified — an admin can enable it on your tech profile'}
          >
            <Download className="h-4 w-4 mr-2" />
            {exportMutation.isPending ? 'Exporting...' : 'Export CSV'}
          </Button>
          {canRecordInspections ? (
            <Button asChild>
              <Link to="/inspections/new">
                <Plus className="h-4 w-4 mr-2" />
                New Inspection
              </Link>
            </Button>
          ) : (
            <Button
              disabled
              title="Requires Inspection Certified — an admin can enable it on your tech profile"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Inspection
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Inspections</p>
                {summaryLoading ? (
                  <Skeleton className="h-6 w-16" />
                ) : (
                  <p className="text-2xl font-bold">{summaryData?.total_count || 0}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Pass Rate</p>
                {summaryLoading ? (
                  <Skeleton className="h-6 w-16" />
                ) : (
                  <p className="text-2xl font-bold">
                    {summaryData?.pass_rate !== undefined
                      ? `${(summaryData.pass_rate * 100).toFixed(1)}%`
                      : '0%'}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Failed</p>
                {summaryLoading ? (
                  <Skeleton className="h-6 w-16" />
                ) : (
                  <p className="text-2xl font-bold">{summaryData?.fail_count || 0}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Conditional</p>
                {summaryLoading ? (
                  <Skeleton className="h-6 w-16" />
                ) : (
                  <p className="text-2xl font-bold">{summaryData?.conditional_count || 0}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <Select
              value={result}
              onValueChange={(value) => updateSearchParams({ result: value === 'all' ? '' : value })}
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="All Results" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Results</SelectItem>
                <SelectItem value="pass">Pass</SelectItem>
                <SelectItem value="fail">Fail</SelectItem>
                <SelectItem value="conditional">Conditional</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={assetId}
              onValueChange={(value) => updateSearchParams({ asset: value === 'all' ? '' : value })}
            >
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="All Assets" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Assets</SelectItem>
                {assetsData?.results?.map((asset) => (
                  <SelectItem key={asset.id} value={String(asset.id)}>
                    {asset.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Collapsible open={showFilters} onOpenChange={setShowFilters}>
              <CollapsibleTrigger asChild>
                <Button variant="outline">
                  <Filter className="h-4 w-4 mr-2" />
                  Date Range
                  <ChevronDown
                    className={`h-4 w-4 ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`}
                  />
                </Button>
              </CollapsibleTrigger>
            </Collapsible>
            {hasActiveFilters && (
              <Button variant="ghost" onClick={clearFilters} className="text-sm">
                <X className="h-4 w-4 mr-2" />
                Clear filters
              </Button>
            )}
          </div>

          <Collapsible open={showFilters} onOpenChange={setShowFilters}>
            <CollapsibleContent className="space-y-4 pt-4 border-t">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date From</Label>
                  <Input
                    type="date"
                    value={dateAfter}
                    onChange={(e) => updateSearchParams({ date_after: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date To</Label>
                  <Input
                    type="date"
                    value={dateBefore}
                    onChange={(e) => updateSearchParams({ date_before: e.target.value })}
                  />
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : isError ? (
            <div className="p-6 text-center text-red-600">
              Failed to load inspections. Please try again.
            </div>
          ) : inspections.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">
                {hasActiveFilters ? 'No inspections match your filters' : 'No inspections yet'}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {hasActiveFilters
                  ? 'Try broadening your search criteria'
                  : 'Create your first inspection to start tracking equipment compliance'}
              </p>
              {hasActiveFilters ? (
                <Button variant="link" onClick={clearFilters} className="mt-2">
                  Clear filters
                </Button>
              ) : canRecordInspections ? (
                <Button asChild variant="outline" className="mt-4">
                  <Link to="/inspections/new">
                    <Plus className="h-4 w-4 mr-2" />
                    New Inspection
                  </Link>
                </Button>
              ) : null}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Asset</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead>Inspector</TableHead>
                  <TableHead>Findings</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inspections.map((inspection) => (
                  <InspectionRow
                    key={inspection.id}
                    inspection={inspection}
                    assetName={getAssetName(inspection.asset)}
                    formatDate={formatDate}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => updateSearchParams({ page: String(Math.max(1, page - 1)) })}
                className={page <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }
              return (
                <PaginationItem key={pageNum}>
                  <PaginationLink
                    onClick={() => updateSearchParams({ page: String(pageNum) })}
                    isActive={page === pageNum}
                    className="cursor-pointer"
                  >
                    {pageNum}
                  </PaginationLink>
                </PaginationItem>
              );
            })}
            <PaginationItem>
              <PaginationNext
                onClick={() => updateSearchParams({ page: String(Math.min(totalPages, page + 1)) })}
                className={page >= totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
}

interface InspectionRowProps {
  inspection: Inspection;
  assetName: string;
  formatDate: (date: string) => string;
}

function InspectionRow({ inspection, assetName, formatDate }: InspectionRowProps) {
  // result is null for pending inspections left for a certified inspector —
  // they are not passes and must not wear the green badge.
  const result = inspection.result;
  const findingsCount = inspection.findings?.length || 0;

  return (
    <TableRow className="cursor-pointer hover:bg-gray-50">
      <TableCell>
        <Link to={`/inspections/${inspection.id}`} className="block">
          {formatDate(inspection.date)}
        </Link>
      </TableCell>
      <TableCell>
        <Link to={`/inspections/${inspection.id}`} className="block">
          <span className="font-medium">{assetName}</span>
        </Link>
      </TableCell>
      <TableCell>
        <Link to={`/inspections/${inspection.id}`} className="block">
          {result ? (
            <Badge className={`${resultColors[result]} flex items-center gap-1 w-fit`}>
              {resultIcons[result]}
              {result.charAt(0).toUpperCase() + result.slice(1)}
            </Badge>
          ) : (
            <Badge className="bg-gray-100 text-gray-800 flex items-center gap-1 w-fit">
              <Clock className="h-4 w-4" />
              Pending
            </Badge>
          )}
        </Link>
      </TableCell>
      <TableCell>
        <Link to={`/inspections/${inspection.id}`} className="block">
          <span className="text-gray-600">{inspection.inspector_name || '-'}</span>
        </Link>
      </TableCell>
      <TableCell>
        <Link to={`/inspections/${inspection.id}`} className="block">
          {findingsCount > 0 ? (
            <span className="text-gray-600">{findingsCount} finding{findingsCount > 1 ? 's' : ''}</span>
          ) : (
            <span className="text-gray-400">None</span>
          )}
        </Link>
      </TableCell>
      <TableCell>
        <Link to={`/inspections/${inspection.id}`} className="block">
          <span className="text-gray-600 truncate max-w-xs block">
            {inspection.notes || '-'}
          </span>
        </Link>
      </TableCell>
    </TableRow>
  );
}
